/**
 * Servicio para gestión de vehículos a través de la nueva API
 * Centraliza todas las operaciones de vehículos con la API backend
 */

import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS } from '../config/api';
import { vehiculoTransforms } from '../utils/dataTransforms';

/**
 * Obtiene todos los vehículos
 * @param {string|number} clienteId - Opcional, filtra por ID de cliente
 * @returns {Promise<Array>} - Lista de vehículos
 */
export const getVehiculos = async (clienteId = null) => {
  try {
    //console.log('🚗 [VehiculosService] Obteniendo vehículos', clienteId ? `para cliente ${clienteId}` : '');
    
    const params = {};
    if (clienteId) {
      params.cliente_id = clienteId;
    }
    
    // Usar cliente normalizado
    const response = await apiHelpers.vehiculos.get(API_ENDPOINTS.VEHICULOS.BASE, { params });
    
    if (response.data) {
      const vehiculosData = response.data.data || response.data.vehiculos || response.data;
      //console.log(`✅ [VehiculosService] ${vehiculosData.length} vehículos encontrados (ya normalizados)`);

      return vehiculoTransforms.fromApiList(vehiculosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [VehiculosService] Error al obtener vehículos:', error);
    throw error;
  }
};

/**
 * Obtiene vehículos por cliente
 * @param {string|number} clienteId - ID del cliente
 * @returns {Promise<Array>} - Lista de vehículos del cliente
 */
export const getVehiculosByCliente = async (clienteId) => {
  try {

    
    const response = await apiHelpers.vehiculos.get(API_ENDPOINTS.VEHICULOS.BY_CLIENTE(clienteId));
    
    if (response.data) {
      const vehiculosData = response.data.data || response.data.vehiculos || response.data;

      return vehiculoTransforms.fromApiList(vehiculosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [VehiculosService] Error al obtener vehículos por cliente:', error);
    throw error;
  }
};

/**
 * Crea un nuevo vehículo
 * @param {Object} vehiculo - Datos del vehículo a crear
 * @returns {Promise<Object>} - Vehículo creado
 */
export const createVehiculo = async (vehiculo) => {
  try {

    
    const vehiculoData = vehiculoTransforms.toApi(vehiculo);
    const response = await apiHelpers.vehiculos.post(API_ENDPOINTS.VEHICULOS.BASE, vehiculoData);
    
    if (response.data) {
      const vehiculoResponse = response.data.data || response.data.vehiculo || response.data;

      return vehiculoTransforms.fromApi(vehiculoResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [VehiculosService] Error al crear vehículo:', error);
    throw error;
  }
};

/**
 * Actualiza un vehículo existente
 * @param {string|number} id - ID del vehículo
 * @param {Object} vehiculo - Datos actualizados
 * @returns {Promise<Object>} - Vehículo actualizado
 */
export const updateVehiculo = async (id, vehiculo) => {
  try {
    //console.log(`🔄 [VehiculosService] Actualizando vehículo ${id}...`);
    
    const vehiculoData = vehiculoTransforms.toApi(vehiculo);
    const response = await apiHelpers.vehiculos.put(API_ENDPOINTS.VEHICULOS.BY_ID(id), vehiculoData);
    
    if (response.data) {
      const vehiculoResponse = response.data.data || response.data.vehiculo || response.data;
      //console.log('✅ [VehiculosService] Vehículo actualizado exitosamente');
      return vehiculoTransforms.fromApi(vehiculoResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [VehiculosService] Error al actualizar vehículo:', error);
    throw error;
  }
};

/**
 * Elimina un vehículo (soft delete)
 * @param {string|number} id - ID del vehículo
 * @returns {Promise<string>} - ID del vehículo eliminado
 */
export const deleteVehiculo = async (id) => {
  try {
    //console.log(`🔄 [VehiculosService] Eliminando vehículo ${id}...`);
    
    await apiHelpers.vehiculos.delete(API_ENDPOINTS.VEHICULOS.BY_ID(id));
    //console.log('✅ [VehiculosService] Vehículo eliminado exitosamente');
    
    return id.toString();
  } catch (error) {
    console.error('❌ [VehiculosService] Error al eliminar vehículo:', error);
    throw error;
  }
};

/**
 * Obtiene un vehículo por su ID
 * @param {string|number} id - ID del vehículo
 * @returns {Promise<Object|null>} - Vehículo encontrado o null
 */
export const getVehiculoById = async (id) => {
  try {
    //console.log(`🔍 [VehiculosService] Obteniendo vehículo ${id}...`);
    
    const response = await apiHelpers.vehiculos.get(API_ENDPOINTS.VEHICULOS.BY_ID(id));
    
    if (response.data) {
      const vehiculoResponse = response.data.data || response.data.vehiculo || response.data;
      //console.log('✅ [VehiculosService] Vehículo obtenido exitosamente');
      return vehiculoTransforms.fromApi(vehiculoResponse);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log('📭 [VehiculosService] Vehículo no encontrado');
      return null;
    }
    console.error('❌ [VehiculosService] Error al obtener vehículo:', error);
    throw error;
  }
};

/**
 * Obtiene un vehículo por su patente
 * @param {string} patente - Patente del vehículo
 * @returns {Promise<Object|null>} - Vehículo encontrado o null
 */
export const getVehiculoByPatente = async (patente) => {
  try {
    //console.log(`🔍 [VehiculosService] Obteniendo vehículo por patente ${patente}...`);
    
    const response = await apiHelpers.vehiculos.get(API_ENDPOINTS.VEHICULOS.BY_PATENTE(patente));
    
    if (response.data) {
      const vehiculoResponse = response.data.data || response.data.vehiculo || response.data;
      //console.log('✅ [VehiculosService] Vehículo por patente obtenido exitosamente');
      return vehiculoTransforms.fromApi(vehiculoResponse);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log('📭 [VehiculosService] Vehículo no encontrado por patente');
      return null;
    }
    console.error('❌ [VehiculosService] Error al obtener vehículo por patente:', error);
    throw error;
  }
};

/**
 * Actualiza el kilometraje de un vehículo
 * @param {string} patente - Patente del vehículo
 * @param {number} kilometraje - Nuevo kilometraje
 * @returns {Promise<Object>} - Vehículo actualizado
 */
export const updateKilometraje = async (patente, kilometraje) => {
  try {
    //console.log(`🔄 [VehiculosService] Actualizando kilometraje del vehículo ${patente}...`);
    
    const response = await apiHelpers.vehiculos.patch(API_ENDPOINTS.VEHICULOS.BY_PATENTE(patente), {
      kilometraje: parseInt(kilometraje, 10)
    });
    
    if (response.data) {
      const vehiculoResponse = response.data.data || response.data.vehiculo || response.data;
      //console.log('✅ [VehiculosService] Kilometraje actualizado exitosamente');
      return vehiculoTransforms.fromApi(vehiculoResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [VehiculosService] Error al actualizar kilometraje:', error);
    throw error;
  }
};

/**
 * Obtiene vehículos por número de teléfono del cliente
 * @param {string} telefono - Número de teléfono del cliente
 * @returns {Promise<Array>} - Lista de vehículos del cliente
 */
export const getVehiculosPorTelefono = async (telefono) => {
  try {
    //console.log(`🔍 [VehiculosService] Obteniendo vehículos por teléfono ${telefono}...`);
    
    const response = await apiHelpers.vehiculos.get(API_ENDPOINTS.VEHICULOS.BASE, {
      params: { telefono_cliente: telefono }
    });
    
    if (response.data) {
      const vehiculosData = response.data.data || response.data.vehiculos || response.data;
      //console.log(`✅ [VehiculosService] ${vehiculosData.length} vehículos encontrados por teléfono`);
      return vehiculoTransforms.fromApiList(vehiculosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [VehiculosService] Error al obtener vehículos por teléfono:', error);
    throw error;
  }
};

/**
 * Funciones de compatibilidad para las funciones específicas del usuario
 */

/**
 * Obtiene vehículos para el cliente actual
 * @param {Object} user - Usuario autenticado
 * @returns {Promise<Array>} - Lista de vehículos del cliente
 */
export const getVehiculosCliente = async (user) => {
  try {
    if (!user || user.TipoUsuario !== 'Cliente' || !user.ClienteID) {
      return [];
    }

    //console.log('🔍 [VehiculosService] Obteniendo vehículos para cliente autenticado...');
    return await getVehiculosByCliente(user.ClienteID);
  } catch (error) {
    console.error('❌ [VehiculosService] Error al obtener vehículos del cliente:', error);
    return [];
  }
};

/**
 * Obtiene vehículos para el cliente actual mediante su teléfono
 * @param {Object} user - Usuario autenticado
 * @returns {Promise<Array>} - Lista de vehículos del cliente
 */
export const getVehiculosClientePorTelefono = async (user) => {
  try {
    if (!user || user.TipoUsuario !== 'Cliente' || !user.ClienteID) {
      return [];
    }

    // En la nueva API, podemos obtener el teléfono del cliente directamente
    // desde la respuesta del endpoint de vehículos por cliente
    //console.log('🔍 [VehiculosService] Obteniendo vehículos por teléfono para cliente autenticado...');
    return await getVehiculosByCliente(user.ClienteID);
  } catch (error) {
    console.error('❌ [VehiculosService] Error al obtener vehículos por teléfono:', error);
    return [];
  }
};

/**
 * Busca vehículos por criterios específicos
 * @param {string} termino - Término de búsqueda
 * @param {number} empresaId - ID de la empresa (opcional para compatibilidad)
 * @returns {Promise<Array>} - Lista de vehículos encontrados
 */
export const searchVehiculos = async (termino, empresaId = null) => {
  try {
    //console.log(`🔍 [VehiculosService] Buscando vehículos con término: ${termino}`);
    
    if (!termino || termino.trim().length < 2) {
      return await getVehiculos(empresaId);
    }
    
    const response = await apiHelpers.vehiculos.get(API_ENDPOINTS.VEHICULOS.BASE, {
      params: { search: termino.trim() }
    });
    
    if (response.data) {
      const vehiculosData = response.data.data || response.data.vehiculos || response.data;
      //console.log('✅ [VehiculosService] Búsqueda exitosa:', vehiculosData.length, 'vehículos');
      return vehiculoTransforms.fromApiList(vehiculosData);
    }
    
    return [];
  } catch (error) {
    console.error(`❌ [VehiculosService] Error al buscar vehículos con término ${termino}:`, error);
    throw error;
  }
};

/**
 * Obtiene estadísticas de vehículos
 * @param {number} empresaId - ID de la empresa (opcional para compatibilidad)
 * @returns {Promise<Object>} - Estadísticas de vehículos
 */
export const getVehiculosStats = async (empresaId = null) => {
  try {
    //console.log('📊 [VehiculosService] Obteniendo estadísticas de vehículos...');
    
    const response = await apiHelpers.vehiculos.get(API_ENDPOINTS.VEHICULOS.STATS || `${API_ENDPOINTS.VEHICULOS.BASE}/stats`);
    
    if (response.data) {
      const stats = response.data.data || response.data;
      const result = {
        totalActivos: stats.totalActivos || stats.total || 0,
        nuevosEsteMes: stats.nuevosEsteMes || stats.nuevos || 0
      };
      
      //console.log('📊 [VehiculosService] Estadísticas calculadas:', result);
      return result;
    }
    
    return {
      totalActivos: 0,
      nuevosEsteMes: 0
    };
  } catch (error) {
    console.error('❌ [VehiculosService] Error al obtener estadísticas:', error);
    // Fallback: retornar valores por defecto en caso de error
    return {
      totalActivos: 0,
      nuevosEsteMes: 0
    };
  }
};

/**
 * Transfiere un vehículo a otro cliente
 * @param {string} patente - Patente del vehículo
 * @param {number} nuevoClienteId - ID del nuevo cliente
 * @param {number} empresaId - ID de la empresa (opcional para compatibilidad)
 * @returns {Promise<Object>} - Vehículo actualizado
 */
export const transferirVehiculo = async (patente, nuevoClienteId, empresaId = null) => {
  try {
    //console.log(`🔄 [VehiculosService] Transfiriendo vehículo ${patente} al cliente ${nuevoClienteId}...`);
    
    const endpoint = API_ENDPOINTS.VEHICULOS.BY_PATENTE ? 
      API_ENDPOINTS.VEHICULOS.BY_PATENTE(patente) : 
      API_ENDPOINTS.VEHICULOS.BY_ID(patente);
    
    const response = await apiHelpers.vehiculos.patch(endpoint, {
      cliente_id: nuevoClienteId
    });
    
    if (response.data) {
      const vehiculoResponse = response.data.data || response.data.vehiculo || response.data;
      //console.log('✅ [VehiculosService] Vehículo transferido exitosamente');
      return vehiculoTransforms.fromApi(vehiculoResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [VehiculosService] Error al transferir vehículo:', error);
    throw error;
  }
};

/**
 * Alias para compatibilidad
 */
export const getVehiculosPorUsuario = getVehiculosCliente;

// Las funciones ya están exportadas individualmente arriba